import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * Public showcase gallery for a specific category.
 * Shows a horizontal row of generated-image cards; clicking opens a lightbox.
 */
export default function ShowcaseGallery({ category, headline, subheadline, dataTestid = "showcase-gallery" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null); // index

  useEffect(() => {
    if (!category) return;
    api.get(`/showcase?category=${encodeURIComponent(category)}`)
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]));
  }, [category]);

  if (!items.length) return null;

  const prev = () => setOpen((i) => (i === 0 ? items.length - 1 : i - 1));
  const next = () => setOpen((i) => (i === items.length - 1 ? 0 : i + 1));

  return (
    <section data-testid={dataTestid} className="relative my-10">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700 flex items-center gap-1.5">
            <Sparkles size={13} /> Made with these commands
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-1.5 tracking-tight text-slate-900">
            {headline || "Real work by real users"}
          </h2>
          {subheadline && <p className="text-sm text-slate-500 mt-1">{subheadline}</p>}
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory"
           style={{ scrollbarWidth: "thin" }}
           data-testid={`${dataTestid}-strip`}>
        {items.map((s, i) => (
          <button key={s.id} onClick={() => setOpen(i)} data-testid={`${dataTestid}-tile-${i}`}
            className="shrink-0 relative snap-start group focus:outline-none">
            <div className="w-[220px] sm:w-[260px] aspect-[4/5] rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200 group-hover:ring-amber-400 transition-all">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} alt={s.caption || s.command_slug || "showcase"}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
            </div>
            {s.command_slug && (
              <span className="absolute bottom-2 left-2 bg-black/80 text-amber-300 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded">
                /{s.command_slug}
              </span>
            )}
          </button>
        ))}
      </div>

      {open !== null && items[open] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid={`${dataTestid}-lightbox`}
             onClick={() => setOpen(null)}>
          <button onClick={(e) => { e.stopPropagation(); setOpen(null); }} aria-label="Close"
            className="absolute top-4 right-4 text-white/80 hover:text-white"><X size={28} /></button>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous"
            className="absolute left-3 sm:left-6 text-white/70 hover:text-white"><ChevronLeft size={32} /></button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next"
            className="absolute right-3 sm:right-6 text-white/70 hover:text-white"><ChevronRight size={32} /></button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${items[open].url}`} alt={items[open].caption || ""}
              className="w-full max-h-[78vh] object-contain rounded-lg" />
            <div className="mt-3 text-white/90 text-sm">
              {items[open].command_slug && (
                <span className="inline-block bg-amber-500/20 text-amber-300 font-black uppercase tracking-wider text-xs px-2.5 py-1 rounded mr-2">
                  /{items[open].command_slug}
                </span>
              )}
              {items[open].caption}
              {items[open].attribution && <span className="text-white/50"> — {items[open].attribution}</span>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
